import { useEventBus } from "$root/event-bus";
import { reportPlatformError } from "$root/api/error-reporting";
import { queryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import type { VirtualInvestmentResponse } from "@eu.mdotm.api/portfolio-lab";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { isCancelledError } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { isBotCompute } from "../common";
import {
	PortfolioLabContext,
	portfolioLabAdapter,
	scenarioIdFromColumns,
	type PortfolioLabComposition,
	type PortfolioLabInvestmentItem,
} from "../ctx";
import { portfolioLabRepository } from "../repository";

/**
 * Re-seed the lab from the server after SphereAI writes to it (MDOT-24442).
 *
 * The chat's post-write refresh (`sphere-ai/botWriteInvalidation.ts`) invalidates every app query
 * and then emits `bot-write-settled`. That heals any screen which RENDERS from a query — but the lab
 * does not: `PortfolioLabContextProvider` seeds `investments$` in a `useState` initializer, i.e. once
 * per mount, and everything below reads the atom. So a refetched `listVirtualInvestments` lands in
 * the cache and goes nowhere, leaving the grid showing the pre-write composition — and, worse, the
 * next local edit would autosave that stale composition straight over the bot's write
 * (`syncComposition` PUTs the whole column; last write wins).
 *
 * Seed-once is deliberate, not an oversight: every lab edit autosaves and the client is
 * authoritative between saves, so reacting to fresh query data in general would fight the user's own
 * typing. The event is what distinguishes "the server changed under me" from that local echo.
 *
 * Rendering from the query instead would not remove this: the reconciliation below — which local
 * edits win, which columns are actually stale — is the domain work, not scaffolding around a seeded
 * atom, and it would have to move into the merge rather than disappear. The lab owning a working copy
 * between saves is what makes its autosave coherent.
 *
 * This re-seed is NOT a remount, unlike the Workspace page's: the lab's remaining atoms are view
 * configuration (widgets, decimals, the splitter) which no lab tool writes, and a remount would also
 * re-run the load-time recompute — a server-side compute per column on every bot write. Only
 * `investments$` and `selectedScenarioId$` are re-seeded, and a column whose composition actually
 * moved takes the server's revisions, so its charts read as stale unless the bot also recomputed it
 * — the widgets never present the previous composition's numbers as current.
 */
const LAB_TOOL_PREFIX = "portfolio_lab-";

/** Columns with a page save on their write lane, which the server is about to accept. */
function hasSaveInFlight(item: PortfolioLabInvestmentItem): boolean {
	return item.pendingWrites > 0 || item.compositionSaveStatus === "saving";
}

/** Weights are compared with a tolerance so float noise alone never counts as a change. */
const WEIGHT_EPSILON = 1e-9;

/**
 * A composition as a weight per holding, keyed by identity rather than position: a nested portfolio
 * by its uuid (with its instruments keyed under it), an instrument by its ticker. Keyed, because the
 * order a re-seed produces (portfolios first, then instruments) is not the order a local `add`
 * leaves behind — comparing by index would report a change where there is none.
 */
function weightsByHolding(composition: PortfolioLabComposition[]): Map<string, number> {
	const weights = new Map<string, number>();
	for (const holding of composition) {
		if (holding.type === "instrument") {
			weights.set(`i:${holding.tickerName}`, holding.weight);
			continue;
		}
		weights.set(`p:${holding.uuid}`, holding.weight);
		for (const inner of holding.instruments) {
			weights.set(`p:${holding.uuid}/${inner.tickerName}`, inner.weight);
		}
	}
	return weights;
}

/**
 * Whether the server's copy of a column differs from the one on screen — i.e. whether the write that
 * just settled actually touched THIS column.
 *
 * Only the inputs a compute depends on are compared: the holdings' weights and the AUM. Monetary
 * amounts are derived from those two, so they cannot diverge on their own; `initialWeight` is the
 * baseline, which a write does not move.
 */
export function columnDiverged(mine: PortfolioLabInvestmentItem, fromServer: PortfolioLabInvestmentItem): boolean {
	if ((mine.aum ?? 0) !== (fromServer.aum ?? 0)) {
		return true;
	}
	const before = weightsByHolding(mine.currentComposition);
	const after = weightsByHolding(fromServer.currentComposition);
	if (before.size !== after.size) {
		return true;
	}
	for (const [key, weight] of after) {
		const previous = before.get(key);
		if (previous === undefined || Math.abs(previous - weight) > WEIGHT_EPSILON) {
			return true;
		}
	}
	return false;
}

/**
 * The lab's next columns: the server's, in the server's order, except where a column has a save in
 * flight or one newer than the server's answer.
 *
 * An in-flight save (an Update-composition commit, or an autosave still unacknowledged) is a write
 * the server is about to accept, so that column is left alone; the re-read waits for the saves it
 * finds before it reads, so one in flight here is an edit made since. A column whose acknowledged
 * `compositionRevision` is ahead of the answer's is left alone too: that save landed after the answer
 * was read, and the server holds it. Every other column becomes what the server says, so a bot write
 * is visible and the next autosave can no longer push a stale composition over it. That includes a
 * column whose own save failed (MDOT-25921): its edits never
 * reached the server, so the server's copy replaces them and the error clears.
 *
 * A column whose composition actually MOVED takes the server's revisions with the rest of its item.
 * Its reports were computed from an earlier revision unless the bot recomputed it, so it joins the
 * lab's manual-refresh flow: the widgets blur and "Update Charts" lights up, and the click recomputes
 * exactly the stale columns (`reloadInvestmentsMetrics({ onlyStale: true })`) from the re-seeded
 * composition. Deliberately not recomputed here — a compute per column is the user's to spend, and
 * that button is where the platform puts the decision.
 *
 * A moved column ALSO keeps its local delta baseline (`initialComposition`/`initialAum`) rather
 * than taking the server's (MDOT-25178), so the Δ reports the bot's move against the baseline the
 * user was already looking at, exactly as it reports a UI edit's pending move. The server's copy is
 * a different baseline: its `initialAum` is the AUM just loaded (`initialAum === aum`), which
 * blanks the AUM Δ, and its `initialComposition` is the baseline the server last took — the
 * column's create-time `initialWeight`, or the last Update AUM — which "Update composition"
 * re-baselines on the client but not on the server. Only the delta baseline is
 * preserved; the weights, monetary amounts and everything else become the server's, so nothing else
 * can go stale. A column the bot CREATED has no local counterpart, so it keeps the server's
 * create-time baseline — correct for a column that never had a previous state to deviate from.
 *
 * A column the write did NOT move keeps its own item and takes only the server's revisions. That
 * matters for the recompute case: `portfolio_lab-recalculate` changes no composition but moves
 * `reportsComputedAtRevision`, so the column reads fresh again, matching the chart queries, which ARE
 * react-query-driven and have already refetched the fresh numbers.
 */
export function reconcileBotWrite(
	current: PortfolioLabInvestmentItem[],
	fromServer: PortfolioLabInvestmentItem[],
): PortfolioLabInvestmentItem[] {
	const local = new Map(current.map((item) => [item.virtualUuid, item]));
	const next = fromServer.map((item) => {
		const mine = local.get(item.virtualUuid);
		if (mine && hasSaveInFlight(mine)) {
			return mine;
		}
		// An answer older than a save this column already had acknowledged was read before that save.
		if (mine && item.compositionRevision < mine.compositionRevision) {
			return mine;
		}
		if (mine && !columnDiverged(mine, item)) {
			return {
				...mine,
				compositionRevision: item.compositionRevision,
				reportsComputedAtRevision: item.reportsComputedAtRevision,
				compositionSaveStatus: "idle",
			} satisfies PortfolioLabInvestmentItem;
		}
		return {
			...item,
			// Preserve the delta baseline the user was already looking at, so the Δ column reports what the
			// bot moved against it (MDOT-25178). A bot-CREATED column has no local counterpart, so it keeps
			// the server's create-time baseline.
			...(mine && {
				initialComposition: mine.initialComposition,
				initialAum: mine.initialAum,
				baselineUnsaved: mine.baselineUnsaved,
			}),
			// A compute SphereAI started ends on its call's result, whatever this write moved (MDOT-25847).
			...(mine?.pendingCompute != null && isBotCompute(mine.pendingCompute) && { pendingCompute: mine.pendingCompute }),
		} satisfies PortfolioLabInvestmentItem;
	});
	// A column the server no longer has is gone — unless a save is in flight on it, which settles its
	// fate. Appended rather than spliced in place: its original neighbours may themselves have been
	// removed.
	const kept = new Set(next.map((item) => item.virtualUuid));
	const orphaned = current.filter((item) => !kept.has(item.virtualUuid) && hasSaveInFlight(item));
	return [...next, ...orphaned];
}

/** The columns a re-read changed: on screen before it, and diverged from what it put there. */
function replacedColumnIds(before: PortfolioLabInvestmentItem[], after: PortfolioLabInvestmentItem[]): Set<string> {
	const mine = new Map(before.map((item) => [item.virtualUuid, item]));
	const replaced = new Set<string>();
	for (const item of after) {
		const previous = mine.get(item.virtualUuid);
		if (previous && columnDiverged(previous, item)) {
			replaced.add(item.virtualUuid);
		}
	}
	return replaced;
}

/**
 * The Lab session as the server holds it once this is called. A list request already on the wire may
 * have left before the write landed, so it is waited out rather than joined; a read that a refetch
 * cancels (the chat's invalidation does) takes that refetch's answer, which left later.
 */
export async function readSessionNow(): Promise<VirtualInvestmentResponse[]> {
	const list = portfolioLabRepository.listVirtualInvestments;
	if (queryClient.isFetching({ queryKey: list.fullQueryKey }) > 0) {
		await list.fetchQuery().catch(noop);
	}
	try {
		return await list.fetchQuery();
	} catch (error) {
		if (isCancelledError(error)) {
			return list.fetchQuery();
		}
		throw error;
	}
}

/**
 * Re-seed the columns from the server after a SphereAI lab write: when the chat's refresh has settled
 * (`bot-write-settled`, keyed to the tool call) and again when the write's result returns in the chat
 * (`bot-lab-write-result`, MDOT-25921), since a write slower than the call-time refresh is read before
 * it lands.
 *
 * Only a mounted lab hears the events. A write that lands while the lab is not mounted is picked up by
 * the page itself, which seeds only from a list fetched for its own mount (`pages/PortfolioLab`).
 *
 * The filter is the tool-name prefix alone, with no uuid matching: there is exactly ONE lab session
 * per user, so every `portfolio_lab-*` write is a write to the page in front of them — and several of
 * those tools (adding a column by name, recomputing the whole session) carry no column uuid to match
 * on anyway. A `portfolio_studio-*` write is deliberately NOT matched: a lab column is a snapshot
 * taken at load, so editing the real portfolio it came from leaves the column as it is.
 */
export function useBotWriteResync(): void {
	const ctx = useMandatoryContext(PortfolioLabContext);
	// Numbers the re-reads: only the newest one asked for writes, so an older read answering last
	// cannot put back what a newer one replaced.
	const latestResync = useRef(0);

	const resync = useCallback(async () => {
		const resyncId = ++latestResync.current;
		// Let every page save on the wire land first, so the read sees it: whatever the server then
		// holds wins.
		await Promise.all(
			readAtom(ctx.investments$)
				.filter(hasSaveInFlight)
				.map((item) => ctx.investmentWriteLanes.run(item.virtualUuid, async () => undefined)),
		);
		const fromServer = await readSessionNow();
		if (resyncId !== latestResync.current) {
			return;
		}
		const current = readAtom(ctx.investments$);
		const next = reconcileBotWrite(current, portfolioLabAdapter.virtualInvestment.toContext(fromServer));
		writeAtom(ctx.investments$, next);
		// An undo step on a replaced column holds its weights from before the write, and undoing would
		// save them over it.
		const replaced = replacedColumnIds(current, next);
		ctx.undoRedo.discard((step) => replaced.has(step.virtualUuid));
		// The scenario is a lab tool's to write too. Read back rather than reconciled: the picker holds no
		// unsaved state of its own, so the server's value simply wins.
		writeAtom(ctx.selectedScenarioId$, () => scenarioIdFromColumns(fromServer));
	}, [ctx]);

	const resyncReportingFailure = useCallback(() => {
		// Fire-and-forget, but never drop the rejection: a failed re-seed leaves the grid showing the
		// pre-write composition, which is the one thing this hook exists to prevent, so it has to be
		// reported rather than swallowed. The columns are left untouched — no partial re-seed.
		resync().catch((error) => {
			reportPlatformError(error, "ERROR", "portfolio", {
				message: `Unable to re-seed the lab after an assistant write (${readAtom(ctx.investments$).length} columns)`,
			});
		});
	}, [resync, ctx.investments$]);

	useEventBus("bot-write-settled", {
		filter: ({ toolNames }) => toolNames.some((name) => name.startsWith(LAB_TOOL_PREFIX)),
		listener: resyncReportingFailure,
	});
	useEventBus("bot-lab-write-result", resyncReportingFailure);
}
